import React, { useEffect, useMemo, useState } from "react";
import { useAccount } from "wagmi";
import { ethers } from "ethers";
import toast from "react-hot-toast";
import { Row, Col, Spinner } from "react-bootstrap";
import SummaryCards from "./components/SummaryCards";
import AdminTablesTabs from "./components/AdminTablesTabs";
import MultisigTransactions from "./components/MultisigTransactions";
import WalletMultisigSection from "./components/WalletMultisigSection";
import { useEthersProvider } from "../../../hooks/useEthersProvider";
import { useEthersSigner } from "../../../hooks/useEthersSigner";
import config from "../../../configs";
import trinityManagerArtifact from "../../../configs/abis/trinity-manager.json";
import trtLogo from "../../../assets/trt.png";
import Header from "../../Layout/Header";
import "./styles.css";

const { abi: trinityManagerAbi } = trinityManagerArtifact;
const ADMIN_ADDRESSES = [
  // "0x70d8e8e389c26ac26b3ce039229ece8a74b01186",
  // "0xbc4e3d7737d1c5a78c84bf88cbc6767febe5e238",
  // "0xf760c209038425300952ed8bd9b0cb27eb726818",
  "0x8d2e7379c22694f759be7dd7059cd1aabec2c445",
  "0x7f164878ACC702Be6B7928A9315932859b9208a0",
  "0x5d191b94af577f73e08563019c8cb5cd9687fbb3",
  "0xbe997eb7b263f99e509db03ece16746c98e9ba50",
  "0x98f2cccd8b5db34b4f960653ddbdd5b82d5dfe93",
];

function SharesAdmin() {
  const { address, isConnected } = useAccount();
  const provider = useEthersProvider();
  const signer = useEthersSigner();
  const managerAddress = config.get("smartContract.TRINITY_MANAGER_ADDRESS");
  const sharesAddressFromConfig = config.get(
    "smartContract.TRINITY_SHARES_ADDRESS"
  );

  const [sharesAddress, setSharesAddress] = useState("");
  const [loadingContract, setLoadingContract] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const handleRefresh = () => setRefreshKey((prev) => prev + 1);
  const [owners, setOwners] = useState([]);

  const authorizedAddresses = owners.length > 0 ? owners : ADMIN_ADDRESSES;

  const isAuthorized =
    address &&
    authorizedAddresses.some(
      (adminAddress) =>
        adminAddress && adminAddress.toLowerCase() === address.toLowerCase()
    );

  useEffect(() => {
    if (!provider) return;
    let cancelled = false;

    const load = async () => {
      setLoadingContract(true);
      try {
        // First, try to get address from config
        if (sharesAddressFromConfig) {
          if (!cancelled) {
            setSharesAddress(sharesAddressFromConfig);
            setLoadingContract(false);
          }
          return;
        }

        // Fallback: try to get from manager contract
        if (managerAddress) {
          const manager = new ethers.Contract(
            managerAddress,
            trinityManagerAbi,
            provider
          );
          const nftAddress = await manager.nft();
          if (!cancelled) {
            setSharesAddress(nftAddress);
          }
        }
      } catch (error) {
        console.error("Failed to fetch TrinityShares address", error);
        toast.error("Unable to determine TrinityShares address");
      } finally {
        if (!cancelled) {
          setLoadingContract(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [provider, managerAddress, sharesAddressFromConfig]);

  useEffect(() => {
    if (!provider || !managerAddress) return;
    let cancelled = false;

    const loadOwners = async () => {
      try {
        const manager = new ethers.Contract(
          managerAddress,
          trinityManagerAbi,
          provider
        );
        const ownersList = await manager.getOwners();
        if (!cancelled) {
          setOwners(ownersList || []);
          console.log("TrinityManager owners:", ownersList);
        }
      } catch (error) {
        console.error("Failed to fetch TrinityManager owners", error);
      }
    };

    loadOwners();
    return () => {
      cancelled = true;
    };
  }, [provider, managerAddress]);

  const content = useMemo(() => {
    if (!isConnected) {
      return (
        <p className="text-center text-white mt-5">
          Connect your wallet to access the Trinity Shares dashboard.
        </p>
      );
    }
    if (!isAuthorized) {
      return (
        <p className="text-center text-white mt-5">
          This dashboard is restricted. Switch to the authorized admin wallet.
        </p>
      );
    }
    if (loadingContract || !sharesAddress) {
      return (
        <div className="text-center text-white mt-5">
          <Spinner animation="border" variant="light" />
          <p className="mt-3">Loading TrinityShares contract...</p>
        </div>
      );
    }

    return (
      <>
        <Row className="justify-content-center shared-space">
          <Col xl={10} lg={10} sm={10} xs={12}>
            <Row className="g-4 mb-4">
              <Col md={12} xs={12}>
                <SummaryCards
                  sharesAddress={sharesAddress}
                  provider={provider}
                  refreshKey={refreshKey}
                />
              </Col>
              {/* <Col md={4} xs={12}>
                <MintPanel
                  signer={signer}
                  onMinted={handleRefresh}
                  adminAddresses={authorizedAddresses}
                  currentUser={address}
                />
              </Col> */}
            </Row>
          </Col>
        </Row>
        <Row className="justify-content-center shared-space mb-5">
          <Col xl={10} lg={10} sm={10} xs={12}>
            <WalletMultisigSection
              provider={provider}
              signer={signer}
              currentUser={address}
              adminAddresses={authorizedAddresses}
              refreshKey={refreshKey}
              onGlobalRefresh={handleRefresh}
            />
          </Col>
        </Row>
        <Row className="justify-content-center shared-space">
          <Col xl={10} lg={10} sm={10} xs={12}>
            <MultisigTransactions
              provider={provider}
              signer={signer}
              currentUser={address}
              adminAddresses={authorizedAddresses}
              isOwner={isAuthorized}
              refreshKey={refreshKey}
            />
          </Col>
        </Row>
        <Row className="justify-content-center pb-5 shared-space">
          <Col xl={10} lg={10} sm={10} xs={12}>
            <AdminTablesTabs
              provider={provider}
              signer={signer}
              sharesAddress={sharesAddress}
              refreshKey={refreshKey}
              isOwner={isAuthorized}
            />
          </Col>
        </Row>
      </>
    );
  }, [
    isConnected,
    isAuthorized,
    loadingContract,
    sharesAddress,
    provider,
    refreshKey,
    signer,
    address,
    authorizedAddresses,
  ]);

  return (
    <div className="live-presale-max-width mt-0 presale-live-background shares-admin-wrapper">
      <Header />
      <Row className="justify-content-center">
        <Col
          xs={12}
          sm={10}
          className="shares-admin-hero shared-margin shared-space"
        >
          <Row className="align-items-center">
            <Col md={12} className="hero-left">
              <div className="hero-title">
                <div className="d-flex align-items-center gap-2">
                  <img src={trtLogo} alt="Trinity Shares" />
                  <h1 className="mb-0">Trinity Shares Dashboard</h1>
                </div>
                <p>Monitor supply, mint allocations, and audit transfers.</p>
                <div className="borderSpace borderSpaceTitle "></div>
              </div>
            </Col>
            {/* <Col md={5}> */}
            {/* <div className="shares-admin-wallet">
                <div className="wallet-info">
                  <p>Balance</p>
                  <h4>{balance} BNB</h4>
                </div>
                <div className="wallet-address">
                  <span>{shortAddress}</span>
                  <IoCopy className="cursor-pointer" onClick={handleCopy} />
                </div>
                <button
                  type="button"
                  className="disconnect-btn"
                  onClick={() => disconnect()}
                  disabled={!isConnected}
                >
                  Disconnect
                </button>
              </div> */}
            {/* </Col> */}
          </Row>
        </Col>
      </Row>
      {content}
    </div>
  );
}

export default SharesAdmin;
